import { useQueryClient } from '@tanstack/react-query';
import i18next from 'i18next';
import { useCallback, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useIsMobile } from '~/components/Breakpoint/Breakpoint.hook';
import { Collapse } from '~/components/Collapse/Collapse';
import { agent } from '~/components/Dosi/Vault';
import { useEmbeddedViewer } from '~/components/Embedded/Embedded.hooks';
import { convertDetectedLanguage } from '~/components/I18n';
import { Iframe } from '~/components/Iframe/Iframe';
import { useMemberErrorHandler, useMemberSecurity } from '~/components/Member/MemberSecurity.hooks';
import { useHandleBodyClick } from '~/components/Modal/Modal.hooks';
import { useSetting } from '~/components/Setting/Setting.hooks';
import styles from './EmbeddedViewer.module.scss';

export const EmbeddedViewer = () => {
  const history = useHistory();
  const isMobile = useIsMobile();
  const handleError = useMemberErrorHandler();
  const { error } = useMemberSecurity({ onError: undefined });
  const { active, close, src } = useEmbeddedViewer();
  const queryClient = useQueryClient();
  const previousActive = useRef(false);
  const { refetch } = useSetting({ enabled: false });

  const onClose = useCallback(() => {
    close();
  }, [close]);

  const { ref } = useHandleBodyClick({
    onClick: () => {
      if (!active) return;
      onClose();
    },
  });

  useEffect(() => {
    if (active && error) {
      handleError(error);
    }
  }, [active, error, handleError]);

  useEffect(() => {
    if (!active) return;

    if (agent || isMobile) {
      if (src) window.location.href = src;
      close();
    }
  }, [active, src, isMobile, history, close]);

  useEffect(() => {
    const invalidateAll = async () => {
      if (!active && previousActive.current !== active) {
        const { data } = await refetch();
        if (data?.language) await i18next.changeLanguage(convertDetectedLanguage(data?.language));
        queryClient.invalidateQueries();
      }
    };
    invalidateAll();

    previousActive.current = active;
  }, [active, queryClient, refetch]);

  return (
    <Collapse ref={ref} className={styles.root} variant="grow" active={active}>
      {src && <Iframe src={src} data-clarity-mask="true" />}
    </Collapse>
  );
};
